/**
 * Loops original BGM from /music. Starts after the first user gesture.
 *
 * Playback goes through Web Audio, not an <audio> element. A long HTML
 * media element registers as exclusive playback and pauses Spotify (and
 * any other app) for as long as this tab is open.
 */

import { synth } from './SoftSynth'

export type BgmScene = 'menu' | 'duel' | 'vault' | 'none'

const VOLUME_KEY = 'yugioh.bgmVolume'
const DEFAULT_VOLUME = 0.34
const FADE_SEC = 0.7

const FILES: Record<Exclude<BgmScene, 'none'>, readonly string[]> = {
  menu: ['/music/menu.mp3'],
  duel: ['/music/duel.mp3', '/music/duel-alt.mp3'],
  vault: ['/music/vault.mp3', '/music/vault-alt.mp3'],
}

const clamp01 = (value: number): number =>
  Math.max(0, Math.min(1, Number.isFinite(value) ? value : DEFAULT_VOLUME))

const readVolume = (): number => {
  try {
    const raw = localStorage.getItem(VOLUME_KEY)
    if (raw === null) return DEFAULT_VOLUME
    return clamp01(Number(raw))
  } catch {
    return DEFAULT_VOLUME
  }
}

/**
 * Asks the browser to mix this page with other apps (Safari). Chrome
 * ignores the API and mixes only when audio is not an HTML media element.
 */
const mixWithOtherApps = (): void => {
  const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession
  if (!session) return
  try {
    session.type = 'ambient'
  } catch {
    /* unsupported type */
  }
}

/**
 * Picks which loop to play from the current Vue route name.
 */
export function bgmSceneForRoute(name: unknown): BgmScene {
  switch (String(name || '')) {
    case 'online-duel':
    case 'duel':
      return 'duel'
    case 'packs':
    case 'collection':
    case 'deck-builder':
    case 'deck-editor':
    case 'daily-missions':
      return 'vault'
    case 'changelog-shots':
    case 'test-ygopro':
      return 'none'
    default:
      return 'menu'
  }
}

interface LiveTrack {
  source: AudioBufferSourceNode
  gain: GainNode
  buffer: AudioBuffer
  /** context.currentTime minus the offset passed to start(). */
  anchor: number
  envFrom: number
  envTo: number
  envStart: number
  envEnd: number
}

class BgmController {
  private ctx: AudioContext | null = null
  private bus: GainNode | null = null
  private live: LiveTrack | null = null
  private scene: BgmScene = 'none'
  private wanted: BgmScene = 'none'
  private currentUrl: string | null = null
  private resumeAt = 0
  private volume = readVolume()
  private unlocked = false
  private sfxHeld = false
  private job = 0
  private inflight: string | null = null
  private readonly decoded = new Map<string, Promise<AudioBuffer | null>>()

  constructor() {
    mixWithOtherApps()
    const unlock = () => this.unlock()
    window.addEventListener('pointerdown', unlock)
    window.addEventListener('keydown', unlock)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.unlocked) synth.unlock()
      this.syncPlayback()
    })
  }

  /**
   * Linear gain 0–1. Persists in this browser.
   */
  getVolume(): number {
    return this.volume
  }

  /**
   * Sets linear gain 0–1 and writes it to localStorage.
   */
  setVolume(value: number): void {
    this.volume = clamp01(value)
    try {
      localStorage.setItem(VOLUME_KEY, String(this.volume))
    } catch {
      /* ignore */
    }
    this.syncPlayback()
  }

  /**
   * Allows autoplay after a click or key. Safe to call often.
   */
  unlock(): void {
    if (this.unlocked) return
    const ctx = synth.unlock()
    if (!ctx) return
    this.unlocked = true
    this.ensureBus(ctx)
    void this.apply(this.wanted)
  }

  /**
   * Duel "Sound off" also silences music. Pack mute does not.
   * SFX volume is separate; this flag is the mute override.
   */
  setSfxMuted(muted: boolean): void {
    this.sfxHeld = muted
    this.syncPlayback()
  }

  /**
   * Switches loop family. Same family does not restart.
   */
  setScene(scene: BgmScene): void {
    this.wanted = scene
    if (!this.unlocked) return
    void this.apply(scene)
  }

  private level(): number {
    if (this.sfxHeld || document.hidden) return 0
    return this.volume
  }

  private ensureBus(ctx: AudioContext): GainNode {
    this.ctx = ctx
    if (this.bus && this.bus.context === ctx) return this.bus
    const bus = ctx.createGain()
    bus.gain.value = this.level()
    bus.connect(ctx.destination)
    this.bus = bus
    return bus
  }

  private writeBus(value: number): void {
    const bus = this.bus
    const ctx = this.ctx
    if (!bus || !ctx) return
    const now = ctx.currentTime
    bus.gain.cancelScheduledValues(now)
    bus.gain.setValueAtTime(value, now)
  }

  private async apply(scene: BgmScene): Promise<void> {
    if (scene === 'none') {
      this.scene = 'none'
      this.currentUrl = null
      this.resumeAt = 0
      this.inflight = null
      this.halt(FADE_SEC)
      return
    }
    if (this.level() <= 0.001) {
      this.halt(0.08)
      return
    }
    if (scene === this.scene && this.live) {
      this.writeBus(this.level())
      return
    }
    const files = FILES[scene]
    const url =
      scene === this.scene && this.currentUrl
        ? this.currentUrl
        : files[Math.floor(Math.random() * files.length)]
    if (url !== this.currentUrl) this.resumeAt = 0
    if (this.inflight === url) return
    this.inflight = url
    await this.crossfadeTo(url, scene)
    if (this.inflight === url) this.inflight = null
  }

  private async crossfadeTo(url: string, scene: BgmScene): Promise<void> {
    const job = ++this.job
    const ctx = this.ctx ?? synth.unlock()
    if (!ctx) {
      this.unlocked = false
      return
    }
    this.ensureBus(ctx)
    const buffer = await this.decode(ctx, url)
    if (job !== this.job || this.wanted !== scene) return
    if (!buffer) {
      this.unlocked = false
      return
    }
    if (this.level() <= 0.001 || !this.bus) {
      const same = url === this.currentUrl
      this.halt(0.08)
      this.scene = scene
      this.currentUrl = url
      if (!same) this.resumeAt = 0
      return
    }
    const track = ctx.createGain()
    track.gain.value = 0
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.loop = true
    source.connect(track)
    track.connect(this.bus)
    const now = ctx.currentTime
    const offset =
      buffer.duration > 0
        ? Math.min(this.resumeAt, Math.max(0, buffer.duration - 0.01))
        : 0
    try {
      source.start(now, offset)
    } catch {
      this.unlocked = false
      return
    }
    track.gain.setValueAtTime(0, now)
    track.gain.linearRampToValueAtTime(1, now + FADE_SEC)
    this.writeBus(this.level())
    const prev = this.live
    this.live = {
      source,
      gain: track,
      buffer,
      anchor: now - offset,
      envFrom: 0,
      envTo: 1,
      envStart: now,
      envEnd: now + FADE_SEC,
    }
    this.scene = scene
    this.currentUrl = url
    this.resumeAt = 0
    if (prev) this.quiet(prev, FADE_SEC)
    for (const key of this.decoded.keys()) {
      if (key !== url) this.decoded.delete(key)
    }
  }

  private decode(ctx: AudioContext, url: string): Promise<AudioBuffer | null> {
    const hit = this.decoded.get(url)
    if (hit) return hit
    const job = (async (): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(url)
        if (!res.ok) return null
        return await ctx.decodeAudioData(await res.arrayBuffer())
      } catch {
        return null
      }
    })().then((buffer) => {
      if (!buffer) this.decoded.delete(url)
      return buffer
    })
    this.decoded.set(url, job)
    return job
  }

  private halt(seconds: number): void {
    const prev = this.live
    if (!prev) return
    this.resumeAt = this.offsetOf(prev)
    this.live = null
    this.quiet(prev, seconds)
  }

  private offsetOf(node: LiveTrack): number {
    const ctx = this.ctx
    const dur = node.buffer.duration
    if (!ctx || dur <= 0) return 0
    const elapsed = ctx.currentTime - node.anchor
    return ((elapsed % dur) + dur) % dur
  }

  /**
   * AudioParam.value is the last assigned value, not the automated one,
   * so the fade envelope is tracked here.
   */
  private envAt(node: LiveTrack, now: number): number {
    const span = node.envEnd - node.envStart
    if (span <= 0 || now >= node.envEnd) return node.envTo
    if (now <= node.envStart) return node.envFrom
    const t = (now - node.envStart) / span
    return node.envFrom + t * (node.envTo - node.envFrom)
  }

  private quiet(node: LiveTrack, seconds: number): void {
    const ctx = node.source.context
    const now = ctx.currentTime
    const end = now + Math.max(0.02, seconds)
    const from = this.envAt(node, now)
    const g = node.gain.gain
    g.cancelScheduledValues(now)
    g.setValueAtTime(from, now)
    g.linearRampToValueAtTime(0, end)
    node.envFrom = from
    node.envTo = 0
    node.envStart = now
    node.envEnd = end
    try {
      node.source.stop(end + 0.03)
    } catch {
      /* already stopped */
    }
  }

  private syncPlayback(): void {
    if (!this.unlocked || !this.ctx || !this.bus) return
    const level = this.level()
    if (level <= 0.001 || this.wanted === 'none') {
      if (this.wanted === 'none') {
        this.scene = 'none'
        this.currentUrl = null
        this.resumeAt = 0
      }
      this.halt(0.08)
      return
    }
    this.writeBus(level)
    if (!this.live) void this.apply(this.wanted)
  }
}

export const Bgm = new BgmController()
